/**
 * Overlay-style scrollbars: the thumb shows while a region is being scrolled
 * and fades out once it settles.
 *
 * CSS has no "is scrolling" state, so this marks the scrolled element with a
 * `data-scrolling` attribute that the stylesheet keys off. The gutter itself is
 * always reserved — only the thumb's colour changes — so revealing it never
 * reflows content.
 */

const ATTRIBUTE = 'data-scrolling';

/** How long the thumb lingers after the last scroll event. */
const IDLE_MS = 900;

export function initScrollbarAutoHide(): () => void {
  const timers = new WeakMap<Element, number>();

  const onScroll = (event: Event) => {
    // Scrolling the page reports `document` as the target; everything else
    // reports the scrolling element itself.
    const target = event.target;
    const element = target instanceof Document ? document.documentElement : target;

    if (!(element instanceof Element)) return;

    // Only touch the DOM when the state actually changes — scroll fires a lot.
    if (!element.hasAttribute(ATTRIBUTE)) element.setAttribute(ATTRIBUTE, '');

    const pending = timers.get(element);
    if (pending !== undefined) clearTimeout(pending);

    timers.set(
      element,
      window.setTimeout(() => {
        element.removeAttribute(ATTRIBUTE);
        timers.delete(element);
      }, IDLE_MS),
    );
  };

  // `scroll` does not bubble, so capture is what catches every nested scroller
  // with a single listener rather than one per container.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });

  return () => document.removeEventListener('scroll', onScroll, { capture: true });
}
